import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Sparkles, Search, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { ScoreBadge, SourceChip } from "@/components/Chips";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PER_PAGE = 25;
const FILTERS = [["all", "All"], ["red", "Red"], ["yellow", "Yellow"], ["green", "Green"], ["protected", "Protected"], ["updated", "Updated"]];
const SORTS = { score_asc: (a, b) => a.score - b.score, score_desc: (a, b) => b.score - a.score,
  views_desc: (a, b) => b.views_30d - a.views_30d, title: (a, b) => a.title.localeCompare(b.title) };

const Row = ({ l, checked, onCheck }) => (
  <tr data-testid={`catalog-row-${l.id}`} className="border-t hover:bg-secondary/40 transition-colors">
    <td className="py-3 pl-4 pr-2 w-8">
      <Checkbox checked={checked} onCheckedChange={onCheck} data-testid={`catalog-select-${l.id}`} />
    </td>
    <td className="py-3 pr-3"><ScoreBadge score={l.score} testid={`catalog-score-${l.id}`} /></td>
    <td className="py-3 pr-3 max-w-0 w-full">
      <Link to={`/app/listings/${l.id}`} data-testid={`catalog-open-${l.id}`} className="block truncate py-3 text-sm hover:text-primary">{l.title}</Link>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="truncate">{l.category}</span>
        {l.bestseller && (
          <span className="inline-flex items-center gap-1 text-[#E9C98B]"><Shield className="h-3 w-3" />{l.shielded ? "Protected bestseller" : "Unlocked"}</span>
        )}
        {l.status === "updated" && <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-3 w-3" />Updated</span>}
        {l.has_draft && <span className="text-primary">Fix ready</span>}
      </div>
    </td>
    <td className="hidden md:table-cell py-3 pr-3 text-right font-mono text-sm">{l.views_30d}</td>
    <td className="hidden md:table-cell py-3 pr-3 text-right font-mono text-sm">{l.favorites_30d}</td>
    <td className="hidden lg:table-cell py-3 pr-4 text-right font-mono text-sm">{l.tag_count}/13</td>
  </tr>
);

export const CatalogTable = ({ listings, source }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("score_asc");
  const [page, setPage] = useState(0);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => {
    const f = listings.filter((l) => {
      if (q && !l.title.toLowerCase().includes(q.toLowerCase())) return false;
      if (filter === "protected") return l.shielded;
      if (filter === "updated") return l.status === "updated";
      if (filter !== "all") return (l.score >= 75 ? "green" : l.score >= 50 ? "yellow" : "red") === filter;
      return true;
    });
    return [...f].sort(SORTS[sort]);
  }, [listings, q, filter, sort]);
  const pages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const view = rows.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  const toggle = (id, v) => {
    const n = new Set(sel);
    if (v) { if (n.size >= 50) return toast.error("You can generate up to 50 fixes per batch."); n.add(id); } else n.delete(id);
    setSel(n);
  };
  const togglePage = (v) => {
    const n = new Set(sel);
    view.forEach((l) => (v ? n.size < 50 && n.add(l.id) : n.delete(l.id)));
    setSel(n);
  };
  const [keywords, setKeywords] = useState("");
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/proposals/batch-generate", { ids: [...sel], keywords });
      navigate(`/app/batch?job=${data.job_id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <section data-testid="catalog-table-section" className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><h2 className="text-xl font-medium">Catalog</h2><SourceChip label={source} testid="catalog-source" /></div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-60">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search titles" className="pl-9 rounded-full" data-testid="catalog-search-input" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 rounded-full" data-testid="catalog-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="score_asc" data-testid="sort-score-asc">Score: low first</SelectItem>
              <SelectItem value="score_desc" data-testid="sort-score-desc">Score: high first</SelectItem>
              <SelectItem value="views_desc" data-testid="sort-views-desc">Most views</SelectItem>
              <SelectItem value="title" data-testid="sort-title">Title A–Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 flex gap-1.5 overflow-x-auto pb-1">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => { setFilter(k); setPage(0); }} data-testid={`catalog-filter-${k}`}
            className={`min-h-[44px] whitespace-nowrap rounded-full border px-4 text-xs transition-colors ${filter === k ? "bg-foreground text-background" : "glass hover:bg-secondary"}`}>
            {l}
          </button>
        ))}
      </div>
      {sel.size > 0 && (
        <div data-testid="batch-action-bar" className="sticky top-10 z-20 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-accent px-4 py-3 fade-up">
          <span className="text-sm"><span className="font-mono" data-testid="batch-selected-count">{sel.size}</span> selected (max 50)</span>
          <input value={keywords} onChange={(e) => setKeywords(e.target.value)} data-testid="batch-keywords-input" aria-label="Your keywords (from eRank, Marmalead or your own research)"
            placeholder="Your keywords (from eRank, Marmalead or your own research)" className="h-11 min-w-[220px] flex-1 rounded-full border border-white/10 bg-background/60 px-4 text-sm outline-none focus:border-[#E9C98B]/60" />
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSel(new Set())} data-testid="batch-clear-button">Clear</Button>
            <Button size="sm" className="rounded-full" onClick={generate} disabled={busy} data-testid="batch-generate-fixes-button">
              <Sparkles className="h-4 w-4 mr-1" />{busy ? "Starting…" : "Generate fixes"}
            </Button>
          </div>
        </div>
      )}
      <div className="mt-4 overflow-hidden rounded-2xl border glass">
        <table className="w-full table-fixed md:table-auto" data-testid="catalog-table">
          <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="py-3 pl-4 pr-2 w-8"><Checkbox data-testid="catalog-select-page" checked={view.length > 0 && view.every((l) => sel.has(l.id))} onCheckedChange={(v) => togglePage(v === true)} /></th>
              <th className="py-3 pr-3 w-16">Score</th>
              <th className="py-3 pr-3">Listing</th>
              <th className="hidden md:table-cell py-3 pr-3 text-right">Views 30d</th>
              <th className="hidden md:table-cell py-3 pr-3 text-right">Favs 30d</th>
              <th className="hidden lg:table-cell py-3 pr-4 text-right">Tags</th>
            </tr>
          </thead>
          <tbody>
            {view.map((l) => <Row key={l.id} l={l} checked={sel.has(l.id)} onCheck={(v) => toggle(l.id, v === true)} />)}
          </tbody>
        </table>
        {!view.length && <p className="p-6 text-sm text-muted-foreground">No listings match.</p>}
      </div>
      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <span data-testid="catalog-count">{rows.length} listings</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="catalog-prev-page">Prev</Button>
          <span className="font-mono">{page + 1}/{pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid="catalog-next-page">Next</Button>
        </div>
      </div>
    </section>
  );
};
